import React, {useEffect, useRef, useState, createContext, useMemo} from 'react';
import './App.scss';
import Header from './components/header';
import Footer from './components/footer';
import {useGetSpeciesImages} from "./helpers/useGetSpeciesImages";
import {useBirdData} from "./helpers/useBirdData";
import {usePeriodFilter} from "./helpers/usePeriodFilter";
import {useDataRequest} from "./helpers/useDataRequest";
// import LogRocket from 'logrocket';
import {EBirdTaxonomyEntry, useEBirdTaxonomy} from "./helpers/useEBirdTaxonomy";
import AppRouter from "./AppRouter";
import {AppScreen} from "./app-screen";
import * as localforage from "localforage";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {getFamilySlug} from "./helpers/helpers";
import {LIFE_JOURNEY_CACHE_KEY} from "./views/discover/constants";
import {clearDerivedBirdDataCaches} from "./helpers/birdDataCache";
import {useDashboardSummary} from "./helpers/useDashboardSummary";

export type AppContextType = {
    lookupByTaxonomicOrder: (taxonomicOrder: number) => EBirdTaxonomyEntry|undefined
}

export type SpeciesFamilyLink = {
    familyName: string,
    familyCommonName?: string,
    familySlug: string
}

export type HomeLocation = {
    locationId: string,
    location: string,
    county: string,
    stateProvince: string,
    countryCode: string,
    latitude: number,
    longitude: number
}

export const AppContext = createContext<AppContextType>({
    lookupByTaxonomicOrder: (taxonomicOrder => undefined)
});

const STORED_EBIRD_DATA_KEY = 'storedEbirdData';
const STORED_HOME_LOCATION_KEY = 'storedHomeLocation';
const DEFAULT_MAP_MODE = 'year';
const FAMILY_EXPLORER_ROUTE = '/family-explorer';

function getYearsFromData(data: any[]): number[] {
    let dataYears: number[] = [];

    for (let row of data) {
        if (!row.date) continue;

        const dataYear = parseInt(row.date.split('-')[0]);

        if (dataYears.indexOf(dataYear) < 0) {
            dataYears.push(dataYear);
        }
    }

    return dataYears.sort().reverse();
}

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const activeMapSpeciesCode = useRef<string|null>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [rawData, setRawData] = useState<any[]>([]);
    const [years, setYears] = useState<number[]|string[]>([]);
    const [homeLocation, setHomeLocation] = useState<HomeLocation | undefined>();

    const { speciesImages, getSpeciesImages } = useGetSpeciesImages();
    const { lookupByTaxonomicOrder } = useEBirdTaxonomy();
    const { hasRequestedData, isDataRequestReady, openMyDataRequest, resetMyDataRequest } = useDataRequest();
    const { lifeList, observationsBySpecies, observationsByLocation, checklist } = useBirdData(rawData);
    const dashboardSummary = useDashboardSummary(rawData, checklist, years);
    const {
        year,
        month,
        months,
        filteredObservations,
        setYear,
        setMonth,
        setTimePeriod: setPeriodFilter,
        setSpeciesFilter,
        resetPeriodFilter
    } = usePeriodFilter(rawData, observationsBySpecies, checklist);

    const hasData = rawData.length > 0;
    const getScreenForPath = (pathname: string) => {
        if (pathname === '/') return AppScreen.Welcome;
        if (pathname === '/upload') return AppScreen.Upload;
        if (pathname === '/home-location') return AppScreen.HomeLocation;
        if (pathname === '/dashboard') return AppScreen.SelectTimePeriod;
        if (pathname === '/map') return AppScreen.Map;
        if (pathname === '/wrap-up') return AppScreen.WrapUp;
        if (pathname === '/discover' || pathname === '/discover/life-journey') return AppScreen.Discover;
        if (pathname === '/big-year' || pathname === '/discover/big-year') return AppScreen.BigYear;
        if (pathname === FAMILY_EXPLORER_ROUTE || pathname.startsWith(`${FAMILY_EXPLORER_ROUTE}/family/`)) return AppScreen.FamilyExplorer;
        if (pathname === '/checklist' || pathname.startsWith('/checklist/family/')) return AppScreen.FamilyExplorer;

        return AppScreen.Welcome;
    }
    const routeScreen = getScreenForPath(location.pathname);
    const screen = isInitialLoading ? AppScreen.Loading : routeScreen;
    const mapMode = searchParams.get('mode') || DEFAULT_MAP_MODE;
    const mapTour = searchParams.get('tour') === 'true';
    const mapSpeciesCode = searchParams.get('species');
    const mapActiveSpecies = mapSpeciesCode
        ? checklist
            .flatMap(family => family.speciesList)
            .find(species => species.speciesCode === mapSpeciesCode)?.taxonomicOrder ?? 0
        : 0;
    const familyByTaxonomicOrder = useMemo(() => {
        const familyLookup = new Map<number, SpeciesFamilyLink>();

        for (const family of checklist) {
            const familyLink = {
                familyName: family.familyName,
                familyCommonName: family.familyCommon,
                familySlug: getFamilySlug(family.familyName)
            };

            for (const species of family.speciesList) {
                familyLookup.set(species.taxonomicOrder, familyLink);
                for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                    familyLookup.set(alternateTaxonomicOrder, familyLink);
                }
            }
        }

        return familyLookup;
    }, [checklist]);
    const parentTaxonomicOrderByTaxonomicOrder = useMemo(() => {
        const parentLookup = new Map<number, number>();

        for (const family of checklist) {
            for (const species of family.speciesList) {
                parentLookup.set(species.taxonomicOrder, species.taxonomicOrder);
                for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                    parentLookup.set(alternateTaxonomicOrder, species.taxonomicOrder);
                }
            }
        }

        return parentLookup;
    }, [checklist]);
    const showHeader = screen !== AppScreen.Loading;
    const showNavigation = hasData && ![AppScreen.Welcome, AppScreen.Upload, AppScreen.HomeLocation].includes(screen);
    const isProtectedRoute = ![AppScreen.Loading, AppScreen.Welcome, AppScreen.Upload].includes(routeScreen);

    const showDashboard = () => {
        navigate(hasData ? '/dashboard' : hasRequestedData ? '/upload' : '/');
    }

    const getDefaultYear = () => {
        const defaultYear = year?.toString() || years[0]?.toString();

        if (defaultYear) {
            setPeriodFilter(defaultYear, 'null');
        }

        return defaultYear;
    }

    const showBigYear = (selectedYear?: string) => {
        const defaultYear = selectedYear || getDefaultYear();
        if (selectedYear) {
            setPeriodFilter(selectedYear, 'null');
        }
        navigate(`/discover/big-year${defaultYear ? `?year=${defaultYear}` : ''}`);
    }

    const showDiscover = () => {
        navigate('/discover');
    }

    useEffect(() => {
        let isActive = true;

        async function resolveInitialScreen() {
            const storedData: any = await localforage.getItem(STORED_EBIRD_DATA_KEY);
            const storedHomeLocation: HomeLocation | null = await localforage.getItem(STORED_HOME_LOCATION_KEY);

            if (!isActive) return;

            if (storedData?.data?.length) {
                setRawData(storedData.data);
                setYears(getYearsFromData(storedData.data));
                if (storedHomeLocation) {
                    setHomeLocation(storedHomeLocation);
                }
                if (location.pathname === '/' || location.pathname === '/upload') {
                    navigate(storedHomeLocation ? '/dashboard' : '/home-location', {replace: true});
                }
                setIsInitialLoading(false);
                return;
            }

            if (isProtectedRoute) {
                navigate(hasRequestedData ? '/upload' : '/', {replace: true});
            }
            setIsInitialLoading(false);
        }

        if (!isDataRequestReady) return;

        void resolveInitialScreen();

        return () => {
            isActive = false;
        }
    }, [hasRequestedData, isDataRequestReady, isProtectedRoute, location.pathname, navigate]);

    useEffect(() => {
        window.scrollTo({top: 0});
    }, [location.pathname]);

    useEffect(() => {
        if (!hasData || screen !== AppScreen.Map) return;

        if (mapMode === 'species' && mapSpeciesCode) {
            const selectedSpecies = checklist
                .flatMap(family => family.speciesList)
                .find(species => species.speciesCode === mapSpeciesCode);

            if (selectedSpecies && activeMapSpeciesCode.current !== mapSpeciesCode) {
                activeMapSpeciesCode.current = mapSpeciesCode;
                setYear('life');
                setMonth('null');
                setSpeciesFilter(selectedSpecies.taxonomicOrder);
                return;
            }

            if (selectedSpecies) return;
        }

        activeMapSpeciesCode.current = null;

        const routeYear = searchParams.get('year') || years[0]?.toString();
        const routeMonth = searchParams.get('month') || 'null';

        if (routeYear && (year?.toString() !== routeYear || month?.toString() !== routeMonth)) {
            setPeriodFilter(routeYear, routeMonth);
        }
    }, [checklist, hasData, mapMode, mapSpeciesCode, month, screen, searchParams, setMonth, setPeriodFilter, setSpeciesFilter, setYear, year, years]);

    useEffect(() => {
        if (!hasData || screen !== AppScreen.BigYear) return;

        const routeYear = searchParams.get('year') || year?.toString() || years[0]?.toString();

        if (routeYear && (year?.toString() !== routeYear || month?.toString() !== 'null')) {
            setPeriodFilter(routeYear, 'null');
        }
    }, [hasData, month, screen, searchParams, setPeriodFilter, year, years]);

    /*useEffect(() => {
        LogRocket.init('z3y7i6/birdmap-explorer');
    }, []);*/

    const requestDataDownload = () => {
        openMyDataRequest();
        navigate('/upload');
    }

    const showUploadData = () => {
        navigate('/upload');
    }

    const resetDataDownloadRequest = () => {
        resetMyDataRequest();
        navigate('/');
    }

    const showMapForTimePeriod = (year: string, month: string) => {
        setPeriodFilter(year, month);
        navigate(`/map?year=${year}&month=${month}&mode=${DEFAULT_MAP_MODE}`);
    }

    const showMap = () => {
        const defaultYear = getDefaultYear();
        navigate(`/map${defaultYear ? `?year=${defaultYear}&month=null&mode=${DEFAULT_MAP_MODE}` : `?mode=${DEFAULT_MAP_MODE}`}`);
    }

    const showTimePeriodSelector = () => {
        navigate('/dashboard');
    }

    const showHomeLocation = () => {
        navigate('/home-location');
    }

    const showTimePeriodSelection = (data: any[]) => {
        setRawData(data);
        navigate(homeLocation ? '/dashboard' : '/home-location');
    }

    const saveHomeLocation = async (location: HomeLocation) => {
        setHomeLocation(location);
        await localforage.setItem(STORED_HOME_LOCATION_KEY, location);
        await localforage.removeItem(LIFE_JOURNEY_CACHE_KEY);
        navigate('/dashboard');
    }

    const skipHomeLocation = async () => {
        setHomeLocation(undefined);
        await localforage.removeItem(STORED_HOME_LOCATION_KEY);
        await localforage.removeItem(LIFE_JOURNEY_CACHE_KEY);
        navigate('/dashboard');
    }

    const resetExplorer = async () => {
        setRawData([]);
        setYears([]);
        resetPeriodFilter();
        navigate('/upload');
    }

    const resetStoredBirdData = async () => {
        await localforage.removeItem(STORED_EBIRD_DATA_KEY);
        await clearDerivedBirdDataCaches();
        await resetExplorer();
    }

    const showWrapUp = () => {
        navigate('/wrap-up');
    }

    const showFamilyExplorer = () => {
        navigate(FAMILY_EXPLORER_ROUTE);
    }

    return (
        <div className="app-wrapper">
            <AppContext.Provider value={{lookupByTaxonomicOrder}}>
                {showHeader ?
                    <Header
                        hasData={hasData}
                        showNavigation={showNavigation}
                        activeScreen={screen}
                        onShowDashboard={showDashboard}
                        onShowMap={showMap}
                        onShowFamilyExplorer={showFamilyExplorer}
                        onShowDiscover={showDiscover}
                    />
                    : null
                }
                <main className="app-main">
                    <AppRouter
                        screen={screen}
                        rawData={rawData}
                        lifeList={lifeList}
                        years={years}
                        months={months}
                        year={year}
                        month={month}
                        mapMode={mapMode}
                        mapTour={mapTour}
                        mapActiveSpecies={mapActiveSpecies}
                        filteredObservations={filteredObservations}
                        observationsBySpecies={observationsBySpecies}
                        observationsByLocation={observationsByLocation}
                        homeLocation={homeLocation}
                        checklist={checklist}
                        dashboardSummary={dashboardSummary}
                        familyByTaxonomicOrder={familyByTaxonomicOrder}
                        parentTaxonomicOrderByTaxonomicOrder={parentTaxonomicOrderByTaxonomicOrder}
                        speciesImages={speciesImages}
                        getSpeciesImages={getSpeciesImages}
                        lookupByTaxonomicOrder={lookupByTaxonomicOrder}
                        onRequestData={requestDataDownload}
                        onShowUploadData={showUploadData}
                        onDataLoaded={showTimePeriodSelection}
                        onSetYears={setYears}
                        onResetDataRequest={resetDataDownloadRequest}
                        onSetMonth={setMonth}
                        onSetYear={setYear}
                        onSetTimePeriod={showMapForTimePeriod}
                        onSetSpeciesFilter={setSpeciesFilter}
                        onSetHomeLocation={saveHomeLocation}
                        onSkipHomeLocation={skipHomeLocation}
                        onResetStoredData={resetStoredBirdData}
                        onStartBigYear={showBigYear}
                        onShowWrapUp={showWrapUp}
                        onShowTimePeriodSelector={showTimePeriodSelector}
                        onShowHomeLocation={showHomeLocation}
                        onShowFamilyExplorer={showFamilyExplorer}
                    />
                </main>
                <Footer />
            </AppContext.Provider>
        </div>
    );
}

export default App;
